import React, { createRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { safeRest } from '@wds/utils';
import { useTheme } from 'react-jss';
import { Fade, Slide } from '@wds/motion';
import { CloseButton, Overlay } from '@wds/internal-components';
import useStyles from './useStyles';

export const SHEET_SIZES = ['sm', 'md', 'lg'];
export const SHEET_PLACEMENTS = ['left', 'right', 'bottom'];

const Sheet = ({
  children,
  onClose,
  show,
  size,
  placement,
  showCloseButton,
  closeButtonLabel,
  blocking,
  offsetTop,
  ...rest
}) => {
  const sheetPanelRef = createRef();
  const theme = useTheme();
  const classes = useStyles({ size, placement, blocking, offsetTop, theme });

  useEffect(() => {
    if (sheetPanelRef.current) {
      sheetPanelRef.current.focus();
    }
  }, [sheetPanelRef, show]);

  const getSlideDirection = () => {
    const direction = 'left';

    if (!placement || placement === 'right') {
      return direction;
    }

    return placement === 'bottom' ? 'up' : 'right';
  };

  const handleClick = () => {
    if (onClose) onClose();
  };

  const closeButton = () => {
    return (
      <CloseButton
        onClick={handleClick}
        variant="dark"
        label={closeButtonLabel}
      />
    );
  };

  return (
    <>
      {blocking && (
        <Fade in={show} speed="fast">
          <div>
            <div className={classes.wrenchSheetOverlay}>
              <Overlay onClick={handleClick} className="wds-overlay" />
            </div>
          </div>
        </Fade>
      )}

      <div className={classes.wrenchSheetContainer}>
        <Slide in={show} direction={getSlideDirection()}>
          <div>
            <div className={classes.wrenchSheet} {...safeRest(rest)}>
              <div
                role="dialog"
                className={classes.wrenchSheetPanel}
                ref={sheetPanelRef}
                tabIndex="-1"
              >
                <If condition={showCloseButton}>{closeButton()}</If>
                {children}
              </div>
            </div>
          </div>
        </Slide>
      </div>
    </>
  );
};

Sheet.propTypes = {
  /**
   * Contents within Sheet
   */
  children: PropTypes.node.isRequired,
  /**
   * Show a close X button at the top right of the sheet
   */
  showCloseButton: PropTypes.bool,
  /**
   * onClose event handler
   * function(event: object) => void
   */
  onClose: PropTypes.func,
  /**
   * if true, sheet will display on screen
   */
  show: PropTypes.bool,
  /**
   * Size variants for sheet
   */
  size: PropTypes.oneOf(SHEET_SIZES),
  /**
   * Placement variants for sheet
   */
  placement: PropTypes.oneOf(SHEET_PLACEMENTS),
  /**
   * The label for close button
   */
  closeButtonLabel: PropTypes.string,
  /**
   * Our sheets block the user from interacting with the underlying page content while a Sheet is open. You may remove that ability and allow your users to interact with the page if it makes sense for your product experience.
   */
  blocking: PropTypes.bool,
  /**
   * Offset spacing from top of the screen
   */
  offsetTop: PropTypes.number,
};

Sheet.defaultProps = {
  showCloseButton: false,
  onClose: undefined,
  show: false,
  size: 'sm',
  placement: 'right',
  closeButtonLabel: 'Close Modal',
  blocking: true,
  offsetTop: 0,
};

export default Sheet;
